import { cn } from '@/utils/format';
import { ClassValue } from 'clsx';

type TypographyProps = {
  children: React.ReactNode;
  className?: ClassValue;
};

export function Heading1({ children, className }: TypographyProps) {
  return (
    <h1
      className={cn(
        'scroll-m-20 mb-2 text-3xl font-extrabold tracking-tight text-balance',
        className,
      )}
    >
      {children}
    </h1>
  );
}

export function Heading2({ children, className }: TypographyProps) {
  return (
    <h2
      className={cn(
        'scroll-m-20 mb-4 border-b pb-2 text-2xl font-semibold tracking-tight first:mt-0',
        className,
      )}
    >
      {children}
    </h2>
  );
}

export function Heading3({ children, className }: TypographyProps) {
  return (
    <h3 className={cn('scroll-m-20 mb-1 text-xl font-semibold tracking-tight', className)}>
      {children}
    </h3>
  );
}

export function Heading4({ children, className }: TypographyProps) {
  return (
    <h4 className={cn('scroll-m-20 mb-1 text-lg font-semibold tracking-tight', className)}>
      {children}
    </h4>
  );
}

export function Paragraph({ children, className }: TypographyProps) {
  return <p className={cn('leading-7 mb-6 text-base', className)}>{children}</p>;
}

export function BlockQuote({ children, className }: TypographyProps) {
  return (
    <blockquote className={cn('mb-6 border-l-2 pl-6 italic', className)}>{children}</blockquote>
  );
}

export function List({
  children,
  type = 'ul',
  className,
}: TypographyProps & { type?: 'ol' | 'ul' }) {
  const Tag = type === 'ol' ? 'ol' : 'ul';
  return (
    <Tag
      className={cn(
        type === 'ol' ? 'list-decimal' : 'list-disc',
        'my-6 ml-6 [&>li]:mt-2',
        className,
      )}
    >
      {children}
    </Tag>
  );
}

export function ListItem({ children, className }: TypographyProps) {
  return <li className={cn('', className)}>{children}</li>;
}

export function LargeText({ children, className }: TypographyProps) {
  return <div className={cn('text-lg font-medium', className)}>{children}</div>;
}

export function Small({ children, className }: TypographyProps) {
  return <small className={cn('text-sm leading-none font-medium', className)}>{children}</small>;
}

export function Muted({ children, className }: TypographyProps) {
  return <p className={cn('text-sm text-muted-foreground', className)}>{children}</p>;
}
